import React, { useMemo } from 'react'
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import DeleteIcon from '@mui/icons-material/Delete'
import MailIcon from '@mui/icons-material/Mail'
import { useTranslation } from 'react-i18next'
import { usePopover } from 'app/menu/components/TopBar/use-popover'
import { TopBarMenuItem } from './TopBarMenuItem'
import { IMessage, LocalizedMessage, MessageTemplate, StatusType, isIMessage } from '@cbgms/api/modules/messages'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import { Alert } from '@carsys/ui'
import { putMessageStatus } from 'app/menu/components/SystemMessage/state/actions'
import { useDispatch } from 'react-redux'
import { useHaynesLicenses } from 'app/settings/components/ThirdPartyLicenses/useHaynesLicenses'
import { formatDateTime } from '@cbgms/base/utils/datetime'
import { useSystemMessages } from 'app/menu/components/TopBar/hooks/useSystemMessages'
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread'
import { loadErroredList } from 'app/settings/state/ExportedInvoices/actions'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import ExportIcon from 'app/menu/components/TopBarV1/assets/ExportIcon.svg'
import { ErroredSoftwareList } from '@cbgms/api'
import groupBy from 'lodash/groupBy'
import { formatEnum } from '@cbgms/base/utils/helpers'
import { useFeature } from '@cbgms/base/feature-flags'

const useStyles = makeStyles({
  messages: {
    minWidth: 370,
    maxWidth: 360,
    maxHeight: 550,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  textOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  item: {
    paddingRight: 80
  }
})

const getUnreadMessages = (messages: IMessage) => {
  return messages.Status === StatusType.Unread
}

export const Message: React.FC<{
  message: IMessage | ErroredSoftwareList
  updateMessage?: (uuid: string, status: StatusType) => void
  hasDivider?: boolean
}> = ({ message, updateMessage, hasDivider }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  if (isIMessage(message)) {
    return (
      <React.Fragment>
        <ListItem className={classes.item} data-testid='message'>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: '#f7f2f2ff' }}>
              <ExportIcon sx={{ fontSize: 22 }} />
            </Avatar>
          </ListItemAvatar>
          <TranslatedSystemMessage message={message} />
          <ListItemSecondaryAction>
            {message.Status === StatusType.Unread && (
              <Tooltip title={t('global.mark_as_read')}>
                <IconButton
                  data-testid='read-button'
                  edge='end'
                  onClick={() => updateMessage && updateMessage(message.UUID, StatusType.Read)}
                  size='large'
                >
                  <MarkAsUnreadIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              data-testid='delete-button'
              edge='end'
              onClick={() => updateMessage && updateMessage(message.UUID, StatusType.Removed)}
              size='large'
            >
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        {message.Template === MessageTemplate.BankHoliday && (
          <Box sx={{ pl: 2, pb: 1 }}>
            <Link href='/settings/absence-schedule' underline='none'>
              {t('messages.go_to_absence_schedule')}
            </Link>
          </Box>
        )}
        {hasDivider && <Divider component='li' />}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <ListItem>
        <Box display='flex' alignItems='flex-start' width='100%'>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: '#f7f2f2ff', mt: 3 }}>
              <ExportIcon sx={{ fontSize: 22 }} />
            </Avatar>
          </ListItemAvatar>
          <Box flex={1}>
            <Box display='flex' alignItems='center' justifyContent='space-between'>
              <Typography variant='subtitle2' fontSize={16} fontWeight={400}>
                {formatEnum('EnumAccountancySoftware', message.AdministrationSoftware)}
              </Typography>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {t('messages.exportErrorInvoiceMessage')}
            </Typography>
          </Box>
        </Box>
      </ListItem>
      {hasDivider && <Divider component='li' />}
    </React.Fragment>
  )
}

export const MessagesMenu: React.FC<{}> = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isTopBarUXChangeForTimerEnabled = useFeature('topbar-ux-change-for-timer')
  const isSmallerThanXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'))

  const { isExpiring, expiryDate, isAcknowledged, acknowledgeExpiry } = useHaynesLicenses()
  const [messagesEl, messagesOpen, handleMessagesClick, handleMessagesClose] = usePopover()
  const { data: messages, refetch } = useSystemMessages()

  const updateMessage = async (uuid: string, status: StatusType) => {
    await dispatch(putMessageStatus(uuid, status))
    await refetch()
  }

  const { data: failedAccountancyExportList = [] } = useQuery<ErroredSoftwareList[]>(
    ['failedAccountancyExportList'],
    async (): Promise<ErroredSoftwareList[]> => {
      const result = await dispatch(loadErroredList())
      return result?.Data || []
    },
    {
      refetchInterval: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000
    }
  )

  type AnyMsg = IMessage | ErroredSoftwareList

  const sortedMessages = useMemo<Record<string, AnyMsg[]>>(() => {
    const mergedMessages = [...(messages || []), ...failedAccountancyExportList].sort((a, b) => {
      const getDate = (msg: AnyMsg) => (isIMessage(msg) ? msg.PublishDate : msg.SentAt)
      return moment(getDate(b)).diff(moment(getDate(a)))
    })

    return groupBy(mergedMessages, (msg: AnyMsg) => moment(isIMessage(msg) ? msg.PublishDate : msg.SentAt).format('DD MMM YY'))
  }, [messages, failedAccountancyExportList])

  const unreadMessages =
    (messages ? messages.filter(getUnreadMessages).length : 0) +
    (isExpiring && !isAcknowledged ? 1 : 0) +
    failedAccountancyExportList.length

  return (
    <>
      {isTopBarUXChangeForTimerEnabled && isSmallerThanXL ? (
        <TopBarMenuItem badge={unreadMessages} onClick={handleMessagesClick} startIcon={<MailIcon />} />
      ) : (
        <TopBarMenuItem isMenu badge={unreadMessages} onClick={handleMessagesClick} startIcon={<MailIcon />}>
          {t('global.messages')}
        </TopBarMenuItem>
      )}
      <Popover
        open={messagesOpen}
        anchorEl={messagesEl}
        onClose={handleMessagesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <List className={classes.messages}>
          {isExpiring && (
            <ListItem>
              <Alert severity='warning'>{t('haynespro.license_expiring', { date: formatDateTime(expiryDate) })}</Alert>
              {!isAcknowledged && (
                <ListItemSecondaryAction>
                  <IconButton data-testid='read-button' edge='end' onClick={acknowledgeExpiry} size='large'>
                    <RadioButtonCheckedIcon color='primary' />
                  </IconButton>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          )}
          {(!messages || messages.length <= 0) &&
            !isExpiring &&
            (!failedAccountancyExportList || failedAccountancyExportList.length <= 0) && (
              <ListItem>
                <Alert color='muted'>{t('messages.messages_read')}</Alert>
              </ListItem>
            )}

          {Object.entries(sortedMessages).map(([date, msgs]) => {
            return (
              <React.Fragment key={date}>
                <ListItem>
                  <Typography variant='subtitle2' fontWeight={500}>
                    {date}
                  </Typography>
                </ListItem>
                {msgs!.map(msg => (
                  <Message
                    key={isIMessage(msg) ? msg.UUID : `${date}_${msg.AdministrationSoftware}`}
                    message={msg}
                    hasDivider
                    updateMessage={isIMessage(msg) ? updateMessage : undefined}
                  />
                ))}
              </React.Fragment>
            )
          })}
        </List>
      </Popover>
    </>
  )
}

interface TranslatedSystemMessage {
  Title: string
  Message: string
  LocalizedMessages: LocalizedMessage[]
}

function TranslatedSystemMessage({ message }: { message: TranslatedSystemMessage }) {
  const { i18n } = useTranslation()
  const localizedMessage = message.LocalizedMessages.find(localized => localized.Locale === i18n.language)

  return <ListItemText primary={localizedMessage?.Title || message.Title} secondary={localizedMessage?.Message || message.Message} />
}

export default MessagesMenu
